'use client';

import React from 'react';
import { ToastProvider, useToast, Card, Button, Heading } from '@ui';
import { withOptionalId } from '@lib/withOptionalId';
import { Section, Container } from '@layout';
import MarkupSnippet from '../markup-snippet/MarkupSnippet';

interface ToastDemoProps {
  sectionID?: string;
  classes?: string;
  containerId?: string;
  containerClasses?: string;
  isSpanish?: boolean;
}

const DemoButtons = ({ isSpanish }: { isSpanish: boolean }) => {
  const { showToast, dismissToast } = useToast();
  const t = (en: string, es: string) => (isSpanish ? es : en);
  const actionToastId = 'action-demo-toast';

  const showActionToast = () => {
    showToast(
      t(
        'This stays open until you join or dismiss it.',
        'Permanece abierto hasta que te unas o lo cierres.'
      ),
      'info',
      t('Room invite', 'Invitacion a sala'),
      actionToastId,
      true,
      <Button
        type='button'
        label={t('Join room', 'Unirse a la sala')}
        className='btn-primary'
        onClick={() => dismissToast(actionToastId)}
      />
    );
  };

  const showPersistentToast = () => {
    const id = 'persistent-demo-toast';
    showToast(
      t('This toast will stay until you close it.', 'Este toast permanecera hasta que lo cierres.'),
      'info',
      t('Persistent Toast', 'Toast persistente'),
      id,
      true
    );
  };

  return (
    <div className='row btn-row gap'>
      <Button
        label={t('Info Toast', 'Toast de info')}
        className='btn-ghost btn-info'
        onClick={() =>
          showToast(
            t('This is an info toast', 'Este es un toast informativo'),
            'info',
            t('Heads up!', 'Atencion')
          )
        }
      />
      <Button
        label={t('Success Toast', 'Toast de exito')}
        className='btn-secondary btn-success'
        onClick={() =>
          showToast(
            t('This is a success toast!', 'Este es un toast de exito'),
            'success',
            t('Success!', 'Exito')
          )
        }
      />
      <Button
        label={t('Warning Toast', 'Toast de advertencia')}
        className='btn-warning'
        onClick={() =>
          showToast(
            t('This is a warning toast!', 'Este es un toast de advertencia'),
            'warning',
            t('Warning!', 'Advertencia')
          )
        }
      />
      <Button
        label={t('Error Toast', 'Toast de error')}
        className='btn-danger'
        onClick={() =>
          showToast(
            t('This is an error toast!', 'Este es un toast de error'),
            'error',
            t('Error!', 'Error')
          )
        }
      />
      <Button
        label={t('Persistent Toast', 'Toast persistente')}
        className='btn-primary'
        onClick={showPersistentToast}
      />
      <Button
        label={t('Toast with action', 'Toast con accion')}
        className='btn-primary'
        onClick={showActionToast}
      />
      <Button
        label={t('Dismiss invite', 'Cerrar invitacion')}
        className='btn-ghost'
        onClick={() => dismissToast(actionToastId)}
      />
    </div>
  );
};

const InlineAlerts = ({ isSpanish }: { isSpanish: boolean }) => {
  const t = (en: string, es: string) => (isSpanish ? es : en);
  return (
    <Card className='card'>
      <div className='card-header'>
        <Heading as='h4'>{t('Inline Alerts', 'Alertas en linea')}</Heading>
      </div>
      <div className='alert alert-success mt-12' role='status'>
        <b>{t('Success:', 'Exito:')}</b>{' '}
        {t('Your settings have been saved.', 'Tu configuracion se ha guardado.')}
      </div>
      <div className='alert alert-info mt-12' role='status'>
        <b>{t('Info:', 'Info:')}</b>{' '}
        {t('New template guidance is available.', 'Hay nueva guia de plantilla disponible.')}
      </div>
      <div className='alert alert-warning mt-12' role='status'>
        <b>{t('Warning:', 'Advertencia:')}</b>{' '}
        {t('A site has overdue tasks.', 'Un sitio tiene tareas vencidas.')}
      </div>
      <div className='alert alert-danger mt-12' role='alert'>
        <b>{t('Error:', 'Error:')}</b>{' '}
        {t(
          'Couldn’t sync with the EDC. Try again.',
          'No se pudo sincronizar con el EDC. Intenta de nuevo.'
        )}
      </div>
    </Card>
  );
};

const ToastsHeading = ({ isSpanish }: { isSpanish: boolean }) => {
  const t = (en: string, es: string) => (isSpanish ? es : en);
  return (
    <>
      <Heading as='h3'>{t('Toasts', 'Notificaciones')}</Heading>
      <p className='sub full'>
        {t(
          'Toasts are used to display brief, auto-dismissing notifications.',
          'Los toasts se usan para mostrar notificaciones breves que se cierran automaticamente.'
        )}
      </p>
    </>
  );
};

const ToastsSubheading = ({ isSpanish }: { isSpanish: boolean }) => {
  const t = (en: string, es: string) => (isSpanish ? es : en);
  return <Heading as='h4'>{t('Toasts', 'Notificaciones')}</Heading>;
};

const ToastsDemo: React.FC<ToastDemoProps> = ({
  sectionID = '',
  classes = '',
  containerId,
  containerClasses,
  isSpanish = false,
}) => (
  <ToastProvider>
    <Section {...withOptionalId(sectionID)} className={classes}>
      <Container containerId={containerId} containerClasses={containerClasses}>
        <ToastsHeading isSpanish={isSpanish} />
        <Card header={<ToastsSubheading isSpanish={isSpanish} />}>
          <DemoButtons isSpanish={isSpanish} />
          <MarkupSnippet
            label={isSpanish ? 'HTML' : 'HTML'}
            copyLabel={isSpanish ? 'Copiar' : 'Copy'}
            copiedLabel={isSpanish ? 'Copiado' : 'Copied'}
            code={`<div class="card toast toast-info">
  <div class="card-header">
    <b>${isSpanish ? 'Invitacion a sala' : 'Room invite'}</b>
    <button class="btn btn-close toast-close" type="button" aria-label="Close">
      <span class="label">×</span>
    </button>
  </div>
  <p class="toast-message">${isSpanish ? 'Permanece abierto hasta que te unas o lo cierres.' : 'This stays open until you join or dismiss it.'}</p>
  <div class="toast-action">
    <button class="btn btn-primary" type="button">
      <span class="label">${isSpanish ? 'Unirse a la sala' : 'Join room'}</span>
    </button>
  </div>
</div>`}
          />
        </Card>
        <InlineAlerts isSpanish={isSpanish} />
      </Container>
    </Section>
  </ToastProvider>
);

export default ToastsDemo;
